import React, { useState } from 'react';
import { X, Map, Play, Coins, ShieldCheck, RefreshCw, BarChart2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useOnboarding } from './OnboardingProvider';
import { useIsCaptain, useIsAdmin } from '../../store/authStore';
import { useTour } from '../tour';
import styles from './Onboarding.module.css';

const FEATURES = [
  {
    icon: Map,
    title: 'Browse Courses',
    description: 'Explore our catalogue of ready-to-run courses.',
  },
  {
    icon: Play,
    title: 'Run Courses',
    description: 'Get a 6-digit access code to allow your cohort to join.',
  },
  {
    icon: Coins,
    title: 'Buy Credits',
    description: 'Purchase credits to run courses. Larger cohorts require more credits.',
  },
  {
    icon: ShieldCheck,
    title: 'Uninterrupted Lessons',
    description: 'Disruption-less, even if you exceed your credit limit.',
  },
  {
    icon: RefreshCw,
    title: 'Re-run Courses',
    description: "Run the same course with different cohorts. Each with it's own analytics.",
  },
  {
    icon: BarChart2,
    title: 'Detailed Analytics',
    description: 'View and download comprehensive course and lesson-level reports.',
  },
] as const;

interface OnboardingModalProps {
  className?: string;
}

export const OnboardingModal: React.FC<OnboardingModalProps> = ({ className = '' }) => {
  const { isOnboardingVisible, hideOnboarding } = useOnboarding();
  const isCaptain = useIsCaptain();
  const isAdmin = useIsAdmin();
  const { startTour } = useTour();
  const [dontShowAgain, setDontShowAgain] = useState(false);

  // Only show for captains who are not admins
  if (!isOnboardingVisible || !isCaptain || isAdmin) {
    return null;
  }

  const handleClose = () => {
    hideOnboarding(dontShowAgain);
  };

  return (
    <div className={`fixed inset-0 flex items-center justify-center z-50 ${styles.modalBackdrop} ${className}`} role="dialog" aria-modal="true" aria-labelledby="onboarding-modal-title">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className={`rounded-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto border-2 ${styles.modalContainer}`}
      >
        {/* Header */}
        <div className={`p-6 relative border-b border-theme-border ${styles.headerGradient}`}>
          <button
            onClick={handleClose}
            className={`absolute top-4 right-4 p-2 rounded-full transition-colors text-theme-text ${styles.closeButton}`}
            aria-label="Close welcome guide"
          >
            <X size={20} />
          </button>

          <div className="flex items-center space-x-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl font-bold font-roboto-mono ${styles.categoryIconCircle}`}>
              !
            </div>
            <div>
              <h1 id="onboarding-modal-title" className="text-2xl font-bold text-theme-primary font-orbitron text-shadow-primary">
                WELCOME, CAPTAIN
              </h1>
              <p className={`text-theme-text font-roboto-mono ${styles.subtitle}`}>
                Here's what you can do on the Armada
              </p>
            </div>
          </div>
        </div>

        {/* Feature cards */}
        <div className="p-6 space-y-6">
          <div className={styles.featureGrid}>
            {FEATURES.map(({ icon: Icon, title, description }) => (
              <div key={title} className={styles.featureCard}>
                <div className="flex items-start space-x-3">
                  <div className={styles.featureIconCircle}>
                    <Icon size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm text-theme-secondary font-orbitron mb-1">
                      {title}
                    </h3>
                    <p className="text-sm text-theme-text leading-relaxed">
                      {description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-4 border-t border-theme-border">
            <div className={styles.dismissRow}>
              <input
                type="checkbox"
                id="dismiss-onboarding"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
              />
              <label htmlFor="dismiss-onboarding">
                Don't show me this again
              </label>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleClose}
                className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors font-roboto-mono"
              >
                Skip Tour
              </button>
              <button
                onClick={() => {
                  hideOnboarding(dontShowAgain);
                  startTour('getting_started');
                }}
                className={styles.getStartedBtn}
              >
                Start Tour
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
